import { useCallback, useEffect, useRef } from "react";
import { useUnifiedAuth } from "../../unified-auth";
import { RESELLER_FLOW_FLAG } from "./constants";

export default function Start() {
  const { loginWithIdP, logout } = useUnifiedAuth();
  const isInitialized = useRef(false);

  const startUnifiedLogin = useCallback(async () => {
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const prompt = urlParams.get("prompt") ?? undefined;

    // The reseller identifier page sets this flag before handing off here. Read and clear it (so it
    // can't leak into a later, unrelated login in this tab) and thread it into appState, so a failed
    // teammate-accept reroutes back to /unified_login/identifier rather than /login/identifier.
    // Guarded: a sessionStorage failure (blocked/partitioned storage) must degrade to a normal
    // non-reseller login, not stall init before loginWithIdP runs. (CLRF-2268 review M1)
    let fromReseller = false;
    try {
      fromReseller = sessionStorage.getItem(RESELLER_FLOW_FLAG) === "true";
      if (fromReseller) {
        sessionStorage.removeItem(RESELLER_FLOW_FLAG);
      }
    } catch (error) {
      console.error("Failed to read reseller flow flag", error);
    }

    // Only logout for prompt=login to force re-authentication
    if (prompt === "login") {
      try {
        await logout({ noRedirect: true });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }

    await loginWithIdP({ fromReseller });
  }, [loginWithIdP, logout]);

  useEffect(() => {
    // Catch so an init failure surfaces (and clears the guard) instead of leaving the page stuck
    // rendering null on an unhandled rejection. (CLRF-2268 review M1)
    startUnifiedLogin().catch((error) => {
      isInitialized.current = false;
      console.error("Failed to start unified login", error);
    });
  }, [startUnifiedLogin]);

  return null;
}
